import * as THREE from 'three';
import { beveledBox, box, gableRoof, mergeParts, part, type Part } from '@/art/geometry';
import { emissive, vertexColored } from '@/art/materials';
import type { PaletteName } from '@/art/palette';
import { CITY } from '@/world/grid';
import { Rng, seedFrom } from '@/util/rng';

/**
 * Havenloods: breed, laag, functioneel.
 *
 * De havenwijk is de oudste wijk van New Amsterdam — gebouwd op handel en vuil werk. Deze
 * loodsen zijn de dominante massa: bakstenen dozen met een zadeldak, een grote laaddeur en
 * een raamritme dat vooral als lichtpatroon telt. Ze zijn niet mooi, ze zijn nuttig, en dat
 * is precies wat de wijk moet uitstralen.
 *
 * Origin: midden van het grondvlak, zodat het gebouw op het stadsgrid snapt.
 */

export const WAREHOUSE = {
  /**
   * De ondergrens is de laaddeur plus zijn stijlen: smaller dan dit past de voorgevel niet
   * om zijn eigen deur heen. Hij stond eerder op 8×8 zonder dat daar iets in de geometrie op
   * wees, en omdat te kleine maten stil geklemd werden werden kadesheds van 4 m diep als 8 m
   * gebouwd — met een botsingsblok van 4. Vandaar dat je door die gevels heen liep.
   */
  minWidth: 5,
  minDepth: 3,
  roofPitch: 0.22,
  windowWidth: 1.1,
  windowHeight: 1.0,
  /** Kans dat een raam verlicht is — een stervende haven staat niet vol licht. */
  litChance: 0.42,
  doorWidth: 3.2,
  doorHeight: 3.4,
  roofSeamSpacing: 1.05,
  triBudget: 1900,
} as const;

export interface WarehouseOptions {
  width: number;
  depth: number;
  floors: number;
  /** Aan welke zijde de laaddeur en de ramen zitten (kijkrichting van de gevel). */
  facing: 'north' | 'south' | 'east' | 'west';
  wall: PaletteName;
  seed: string;
}

const DEFAULTS: WarehouseOptions = {
  width: 12,
  depth: 10,
  floors: 2,
  facing: 'south',
  wall: 'nightBrick',
  seed: 'warehouse',
};

export function buildWarehouse(options: Partial<WarehouseOptions> = {}): THREE.Group {
  const o = { ...DEFAULTS, ...options };
  // Stil klemmen op de minimummaat was de bron van de "je loopt door de gevel"-bug: de loods
  // werd 8 m diep gebouwd terwijl de leveldata 4 zei, en collision en validator geloofden de
  // leveldata. Een te kleine maat is een fout in de data en hoort hard te falen.
  if (o.width < WAREHOUSE.minWidth || o.depth < WAREHOUSE.minDepth) {
    throw new Error(
      `Havenloods ${o.seed} is ${o.width}x${o.depth} m; minimaal is `
      + `${WAREHOUSE.minWidth}x${WAREHOUSE.minDepth} m. Kies een grotere maat of een ander gebouwtype.`,
    );
  }
  const { width, depth } = o;
  const wallHeight = o.floors * CITY.floorHeight;
  const rng = new Rng(seedFrom(o.seed));

  const group = new THREE.Group();
  group.name = 'NA_Building_Warehouse';
  group.rotation.y = facingRotation(o.facing);

  const solid: Part[] = [];
  const lights: Part[] = [];

  // Romp: de bevel vangt het straatlicht op de verticale hoeken en maakt de doos leesbaar
  // als volume in plaats van als silhouetgat.
  solid.push(part(beveledBox(width, wallHeight, depth, 0.05).translate(0, wallHeight / 2, 0), o.wall));

  // Plint: een donkere band die het gebouw op de grond zet.
  solid.push(part(box(width + 0.12, 0.5, depth + 0.12).translate(0, 0.25, 0), 'nightStone'));
  solid.push(part(box(width + 0.24, 0.18, 0.22).translate(0, wallHeight - 0.08, depth / 2 + 0.11), 'darkSteel'));
  solid.push(part(box(width + 0.24, 0.18, 0.22).translate(0, wallHeight - 0.08, -depth / 2 - 0.11), 'darkSteel'));
  solid.push(part(box(0.14, wallHeight - 0.4, 0.14).translate(-width / 2 - 0.1, wallHeight / 2 + 0.08, depth / 2 + 0.1), 'darkSteel'));
  solid.push(part(box(0.14, wallHeight - 0.4, 0.14).translate(width / 2 + 0.1, wallHeight / 2 + 0.08, depth / 2 + 0.1), 'darkSteel'));

  // Zadeldak met overstek, in verweerd metaal.
  const roofHeight = width * WAREHOUSE.roofPitch;
  const roof = gableRoof(width, depth, roofHeight, 0.35);
  roof.translate(0, wallHeight, 0);
  solid.push(part(roof, 'rustOrange'));
  solid.push(part(box(width + 0.55, 0.16, 0.22).translate(0, wallHeight + 0.08, depth / 2 + 0.38), 'darkSteel'));
  solid.push(part(box(width + 0.55, 0.16, 0.22).translate(0, wallHeight + 0.08, -depth / 2 - 0.38), 'darkSteel'));

  for (let z = -depth / 2 + 0.65; z <= depth / 2 - 0.65; z += WAREHOUSE.roofSeamSpacing) {
    solid.push(part(box(0.09, 0.08, depth * 0.09).rotateX(Math.PI / 2).translate(-width * 0.26, wallHeight + roofHeight * 0.5 + 0.08, z), 'darkSteel'));
    solid.push(part(box(0.09, 0.08, depth * 0.09).rotateX(Math.PI / 2).translate(width * 0.26, wallHeight + roofHeight * 0.5 + 0.08, z), 'darkSteel'));
  }

  for (const x of [-width * 0.28, width * 0.18]) {
    solid.push(
      part(box(1.45, 0.26, 0.82).translate(x, wallHeight + roofHeight + 0.2, -depth * 0.12), 'darkSteel'),
      part(box(1.18, 0.08, 0.62).translate(x, wallHeight + roofHeight + 0.38, -depth * 0.12), 'slateGrey'),
    );
  }

  // Laaddeur in de voorgevel: groot, donker, en met een luifel erboven.
  const front = depth / 2;
  solid.push(
    part(
      box(WAREHOUSE.doorWidth, WAREHOUSE.doorHeight, 0.18).translate(
        0,
        WAREHOUSE.doorHeight / 2,
        front + 0.02,
      ),
      'charcoal',
    ),
  );
  solid.push(
    part(
      box(WAREHOUSE.doorWidth + 0.8, 0.16, 0.9).translate(
        0,
        WAREHOUSE.doorHeight + 0.2,
        front + 0.35,
      ),
      'darkSteel',
    ),
  );
  solid.push(part(box(WAREHOUSE.doorWidth + 1.15, 0.16, 0.24).translate(0, WAREHOUSE.doorHeight + 0.42, front + 0.2), 'rustOrange'));
  solid.push(part(box(WAREHOUSE.doorWidth + 0.42, 0.16, 0.22).translate(0, WAREHOUSE.doorHeight + 0.08, front + 0.12), 'nightStone'));
  solid.push(part(box(0.18, WAREHOUSE.doorHeight + 0.2, 0.18).translate(-WAREHOUSE.doorWidth / 2 - 0.14, WAREHOUSE.doorHeight / 2, front + 0.12), 'nightStone'));
  solid.push(part(box(0.18, WAREHOUSE.doorHeight + 0.2, 0.18).translate(WAREHOUSE.doorWidth / 2 + 0.14, WAREHOUSE.doorHeight / 2, front + 0.12), 'nightStone'));
  solid.push(part(box(WAREHOUSE.doorWidth + 1.05, 0.16, 0.72).translate(0, 0.08, front + 0.36), 'pavement'));
  for (const x of [-1.2, 0, 1.2]) {
    solid.push(part(box(0.08, WAREHOUSE.doorHeight * 0.82, 0.08).translate(x, WAREHOUSE.doorHeight * 0.46, front + 0.14), 'darkSteel'));
  }
  for (const x of [-WAREHOUSE.doorWidth * 0.35, WAREHOUSE.doorWidth * 0.35]) {
    solid.push(part(box(0.62, 0.1, 0.08).translate(x, 2.42, front + 0.17), 'slateGrey'));
  }

  // Raamritme op de voorgevel. Verlichte ramen zijn emissief — ze doen het meeste werk in
  // de nachtscène en kosten geen lichtbron.
  const columns = Math.max(2, Math.floor((width - 2) / CITY.facadeModule));
  const spacing = width / (columns + 1);

  for (let floor = 0; floor < o.floors; floor++) {
    const y = floor * CITY.floorHeight + CITY.floorHeight * 0.62;
    for (let column = 0; column < columns; column++) {
      const x = -width / 2 + spacing * (column + 1);
      // De begane grond is bij de laaddeur dichtgezet.
      if (floor === 0 && Math.abs(x) < WAREHOUSE.doorWidth * 0.75) continue;

      const window = box(WAREHOUSE.windowWidth, WAREHOUSE.windowHeight, 0.1).translate(
        x,
        y,
        front + 0.03,
      );
      if (rng.chance(WAREHOUSE.litChance)) {
        lights.push(part(window, 'windowWarm'));
      } else {
        solid.push(part(window, 'charcoal'));
      }
      solid.push(
        part(box(WAREHOUSE.windowWidth + 0.22, 0.11, 0.14).translate(x, y + WAREHOUSE.windowHeight / 2 + 0.08, front + 0.1), 'nightStone'),
        part(box(WAREHOUSE.windowWidth + 0.26, 0.12, 0.16).translate(x, y - WAREHOUSE.windowHeight / 2 - 0.08, front + 0.11), 'nightStone'),
        part(box(0.08, WAREHOUSE.windowHeight + 0.14, 0.12).translate(x - WAREHOUSE.windowWidth / 2 - 0.05, y, front + 0.1), 'darkSteel'),
        part(box(0.08, WAREHOUSE.windowHeight + 0.14, 0.12).translate(x + WAREHOUSE.windowWidth / 2 + 0.05, y, front + 0.1), 'darkSteel'),
        part(box(0.07, WAREHOUSE.windowHeight, 0.12).translate(x, y, front + 0.11), 'darkSteel'),
      );
    }
  }

  for (let z = -depth / 2 + 1.2; z <= depth / 2 - 1.2; z += 1.45) {
    solid.push(part(box(0.08, 0.12, 0.8).translate(-width / 2 - 0.08, wallHeight + 0.1, z), 'darkSteel'));
    solid.push(part(box(0.08, 0.12, 0.8).translate(width / 2 + 0.08, wallHeight + 0.1, z), 'darkSteel'));
  }
  for (const x of [-width / 2 + 1.25, width / 2 - 1.25]) {
    solid.push(part(box(0.18, wallHeight - 0.7, 0.2).translate(x, wallHeight / 2 + 0.08, -depth / 2 - 0.08), 'darkSteel'));
  }
  for (let x = -width / 2 + 1.6; x <= width / 2 - 1.6; x += 2.6) {
    solid.push(part(box(1.05, 0.7, 0.1).translate(x, wallHeight * 0.55, -depth / 2 - 0.06), 'charcoal'));
    solid.push(part(box(1.22, 0.08, 0.12).translate(x, wallHeight * 0.55 + 0.43, -depth / 2 - 0.1), 'nightStone'));
  }

  // De romp die de speler tegenhoudt, uitgedrukt in exact de variabelen waarmee hij gebouwd
  // is. `world/footprints.ts` leest dit als enige bron van waarheid voor collision en
  // validatie, zodat leveldata, mesh en botsingsblok niet uit elkaar kunnen lopen.
  group.userData.naFootprint = { width, depth, height: wallHeight };

  const shell = new THREE.Mesh(mergeParts(solid), vertexColored());
  shell.name = 'shell';
  shell.castShadow = true;
  shell.receiveShadow = true;
  shell.userData.naOccluder = true;
  group.add(shell);

  if (lights.length > 0) {
    // Verlichte ramen krijgen een basic (niet-belicht) materiaal, anders dooft het
    // nachtlicht ze uit. Ze werpen bewust geen schaduw.
    const glow = new THREE.Mesh(mergeParts(lights), emissive('windowWarm', 0.9));
    glow.name = 'windows';
    group.add(glow);
  }

  return group;
}

function facingRotation(facing: WarehouseOptions['facing']): number {
  switch (facing) {
    case 'south':
      return 0;
    case 'north':
      return Math.PI;
    case 'east':
      return -Math.PI / 2;
    case 'west':
      return Math.PI / 2;
  }
}
